import { Component, DestroyRef, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, GroupDescriptor, filterBy } from '@progress/kendo-data-query';
import { GridViewManager } from 'src/app/core/model/grid-view-manager';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { SearchHelperService } from 'src/app/core/utility/search-helper.service';
import { AccumulatorEdit } from '../model/accumulatorEdit';
import { AccumulatorService } from 'src/app/core/service/accumulator.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { finalize } from 'rxjs/operators';

@Component({
  standalone: false,
  selector: 'app-accumulator-list',
  templateUrl: './accumulator-list.component.html',
  styleUrls: ['./accumulator-list.component.scss']
})
export class AccumulatorListComponent implements OnInit
{
  public pageSizes = [5, 10, 25, 50, 100];
  public view: GridViewManager;
  public deleteDialogOpened = false;
  public enableAdvanced = false;
  public count: number;
  public isNew = false;
  public editDataItem: AccumulatorEdit;
  public selectedAccumulator: AccumulatorEdit;
  public filterText = '';

  constructor(
    private accumulatorService: AccumulatorService,
    private messageHandlerService: MessageHandlerService,
    private appConfigService: AppConfigService,
    private searchHelperService: SearchHelperService,
    public intl: IntlService,
    private readonly destroyRef: DestroyRef) { }

  public ngOnInit(): void
  {
    this.view = new GridViewManager(this.appConfigService.getConfig().pageSize);
    this.loadGridData();
  }

  /**
   * Closes a component
   *
   * @param component Name of component to be closed.
   */
  public close(component: string): void
  {
    this[component + 'Opened'] = false;
  }

  /**
   * Opens a component
   *
   * @param component Name of component to be opened.
   */
  public open(component: string): void
  {
    this[component + 'Opened'] = true;
  }

  /**
   * Button action handler.
   *
   * @param confirm Confirm the action: yes/no
   */
  public deleteAction(confirm: boolean): void
  {
    if (confirm)
    {
      // Proceed with the deletion
      const accumulatorId = this.selectedAccumulator.id;
      this.accumulatorService.deleteAccumulator(accumulatorId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () =>
        {
          this.view.data = this.view.data.filter(e => e.id !== accumulatorId);
          this.view.refreshView();
          this.count = this.view.data.length;
          this.messageHandlerService.displaySuccess('ACCUMULATOR-DELETED-SUCCESSFULLY');
        },
        error: (errorResponse) =>
        {
          this.messageHandlerService.displayError(errorResponse);
        }
      });
    }
    // post-deletion cleanup and refresh grid
    this.close('deleteDialog');
    this.selectedAccumulator = null;
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Group change event handler.
   *
   * @param groups List of GroupDescriptors
   */
  public groupChange(groups: GroupDescriptor[]): void
  {
    this.view.setGroup(groups);
  }

  /**
   * Sets the user to delete, and opens the delete user dialog.
   */
  public deleteHandler(id: number): void
  {
    // Check if in use
    this.accumulatorService.existsAccumulator(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (inUseResponse) =>
      {
        // If in use, avoid deletion
        if (inUseResponse)
        {
          this.messageHandlerService.displayWarning('ACCUMULATOR-IN-USE-CANNOT-DELETE');
        }
        else
        {
          this.accumulatorService.getAccumulatorById(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
            next: (data) =>
            {
              this.selectedAccumulator = data as AccumulatorEdit;
              this.open('deleteDialog');
            },
            error: (errorResponse) =>
            {
              this.messageHandlerService.displayError(errorResponse);
            }
          });
        }
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public addHandler(): void
  {
    this.isNew = true;
    this.editDataItem = new AccumulatorEdit();
  }

  public editHandler(id: number): void
  {
    this.isNew = false;
    this.accumulatorService.getAccumulatorById(id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) =>
      {
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        this.editDataItem = data;
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public saveChangesHandler(accumulator: AccumulatorEdit): void
  {
    this.accumulatorService.saveAccumulator(accumulator, this.isNew, accumulator.id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) =>
      {
        // Must convert the lastModifiedOn into Date object
        data.lastModifiedOn = new Date(data.lastModifiedOn);
        this.view.addOrUpdateGridRow(data);
        this.count = this.view.data.length;  // Update count

        this.editDataItem = undefined;
        this.messageHandlerService.displaySuccess('ACCUMULATOR-SAVED-SUCCESSFULLY');
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  public cancelChangesHandler(): void
  {
    this.editDataItem = undefined;
  }

  /**
   * Filtering by search field
   */
  public filterChange(filter: CompositeFilterDescriptor): void
  {
    this.view.state.filter = filter;
    this.view.gridData.data = filterBy(this.view.data, filter);
    this.count = this.view.gridData.data.length;
    this.view.refreshView();
  }

  public searchFieldChange(filterText: string): void
  {
    this.view.state.skip = 0;
    const root = this.searchHelperService.fieldChange(filterText, this.view.state);
    this.filterText = filterText;
    this.filterChange(root);
  }

  private loadGridData(): void
  {
    this.view.startLoading();
    this.accumulatorService.getAccumulatorList().pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.view.stopLoading())).subscribe({
      next: (data) =>
      {
        data.forEach(d => { d.lastModifiedOn = new Date(d.lastModifiedOn); });
        this.count = data.length;
        this.view.setData(data);
        this.view.setSort([{ field: 'name', dir: 'asc' }]);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }
}
